import { useState, useEffect } from 'react'
import { Menu } from '../../../types/menu'
import { embedGenerator, EmbedConfig } from '../../../services/embedGenerator'

interface EmbedGeneratorProps {
  menu: Menu
  onChange?: (filteredMenu: Menu) => void
}

export const EmbedGenerator = ({
  menu,
  onChange,
}: EmbedGeneratorProps) => {
  const config: EmbedConfig = {
    showPrices: true,
    showDescriptions: true,
  }

  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'complete'>(
    'complete'
  )
  const [copied, setCopied] = useState(false)
  const [openAccordions, setOpenAccordions] = useState<{
    option1: boolean
    option2: boolean
    option3: boolean
  }>({
    option1: false,
    option2: false,
    option3: false
  })

  const createFilteredMenu = (baseMenu: Menu, config: EmbedConfig): Menu => {
    return {
      ...baseMenu,
      sections: baseMenu.sections.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          price: config.showPrices ? item.price : undefined,
          description: config.showDescriptions ? item.description : undefined,
        })),
      })),
    }
  }

  const filteredMenu = createFilteredMenu(menu, config)
  const embedCode = embedGenerator.generateEmbedCode(filteredMenu, config)
  
  const toggleAccordion = (option: 'option1' | 'option2' | 'option3') => {
    setOpenAccordions(prev => ({
      ...prev,
      [option]: !prev[option]
    }))
  }

  // Initialize filtered menu on mount and when menu changes
  useEffect(() => {
    if (onChange) {
      const filteredMenu = createFilteredMenu(menu, config)
      onChange(filteredMenu)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, onChange])

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const downloadHTML = () => {
    const blob = new Blob([embedCode.complete], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${menu.name.toLowerCase().replace(/\s+/g, '-')}-menu.html`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const getCodeContent = (): string => {
    switch (activeTab) {
      case 'html':
        return embedCode.html
      case 'css':
        return embedCode.css
      case 'complete':
        return embedCode.complete
      default:
        return embedCode.complete
    }
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <h2 className="text-left text-3xl font-bold text-custom-dark">
            Generate Embed Code
          </h2>
          <p className="text-left text-gray-600 mt-2">
            Create embeddable HTML code for your menu that you can use on any
            website
          </p>
        </div>
      </div>

      {/* Code Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-custom-dark">Embed Code</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => copyToClipboard(getCodeContent())}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center space-x-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <span>{copied ? 'Copied!' : 'Copy'}</span>
            </button>
            <button
              onClick={downloadHTML}
              className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm flex items-center space-x-2"
            >
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <span>Download</span>
            </button>
          </div>
        </div>

        {/* Code Tabs */}
        <div className="border border-gray-200 rounded-lg">
          <div className="flex border-b border-gray-200">
            {[
              { key: 'complete', label: 'Complete HTML' },
              { key: 'html', label: 'HTML Only' },
              { key: 'css', label: 'CSS Only' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`px-4 py-2 text-sm font-medium ${
                  activeTab === tab.key
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-4">
            <pre className="bg-brand-secondary text-gray-100 p-4 rounded text-xs overflow-x-auto max-h-96">
              <code>{getCodeContent()}</code>
            </pre>
          </div>
        </div>
      </div>

      {/* Instructions */}
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-custom-dark mb-2">
            How to Add to Your Website
          </h2>
          <p className="text-gray-600">How to embed this menu on your website</p>
        </div>

        {/* Accordion Section */}
        <div className="space-y-4">
          {/* Option 1 Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleAccordion('option1')}
              className="w-full px-4 py-3 text-left flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
            >
              <h3 className="text-lg font-semibold text-custom-dark">
                Option 1: Complete HTML file
              </h3>
              <svg
                className={`w-5 h-5 transform transition-transform ${
                  openAccordions.option1 ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                openAccordions.option1 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 border-t border-gray-200">
                <ol className="space-y-2 list-decimal list-inside">
                  <li>Copy the "Complete HTML" code above</li>
                  <li>Save it as a .html file (e.g., menu.html)</li>
                  <li>Upload to your website</li>
                  <li>Link to it or embed in an iframe</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Option 2 Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleAccordion('option2')}
              className="w-full px-4 py-3 text-left flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
            >
              <h3 className="text-lg font-semibold text-custom-dark">
                Option 2: Embed in existing page (best for search)
              </h3>
              <svg
                className={`w-5 h-5 transform transition-transform ${
                  openAccordions.option2 ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                openAccordions.option2 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 border-t border-gray-200">
                <ol className="space-y-2 list-decimal list-inside">
                  <li>Copy the "HTML Only" code and paste it where you want the menu</li>
                  <li>Copy the "CSS Only" code and add it to your page's &lt;head&gt; section inside &lt;style&gt; tags</li>
                </ol>
                <p className="mt-3 text-sm text-gray-600">
                  This puts the menu directly on your page, so search engines credit it to your site. An iframe or a
                  separate file works, but Google sees the menu as a different page.
                </p>
              </div>
            </div>
          </div>

          {/* Option 3 Accordion */}
          <div className="border border-gray-200 rounded-lg">
            <button
              onClick={() => toggleAccordion('option3')}
              className="w-full px-4 py-3 text-left flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors rounded-t-lg"
            >
              <h3 className="text-lg font-semibold text-custom-dark">
                Option 3: Using an iframe
              </h3>
              <svg
                className={`w-5 h-5 transform transition-transform ${
                  openAccordions.option3 ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                openAccordions.option3 ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <div className="p-4 border-t border-gray-200">
                <p className="mb-3">Use this HTML code to embed the menu as an iframe:</p>
                <pre className="bg-gray-800 text-gray-100 p-3 rounded text-sm overflow-x-auto">
                  <code>
                    &lt;iframe src="path/to/your/menu.html" width="100%" height="600" frameborder="0"&gt;&lt;/iframe&gt;
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="space-y-6 pt-4">
          <div>
            <h3 className="text-lg font-semibold text-custom-dark mb-3">Features</h3>
            <ul className="space-y-2 list-disc list-inside text-gray-700">
              <li>Automatically responsive (works on all screen sizes)</li>
              <li>Uses your chosen colours; fonts and sizes match your website</li>
              <li>Real text that screen readers, Google and AI assistants can read, unlike a PDF or photo</li>
              <li>Includes structured menu data (schema.org) so search engines understand dishes and prices</li>
              <li>Lightweight: HTML and CSS only. The one script tag is menu data, not code that runs.</li>
            </ul>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-custom-dark mb-3">
              Customization
            </h3>
            <p className="text-gray-700">
              The embed uses the colours and column layout you chose with "Edit styles". Fonts and text sizes
              come from your website, so the menu matches the rest of your pages. To change colours, go back to
              editing and use "Edit styles" before copying the code.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
